import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useForm } from 'react-hook-form';
import { isBoolean, isString } from 'lodash';
import { setCommissionDeviceAcquisition, resetAcqInfo } from 'redux/Commissioning';
import Button from 'components/shared/Button';
import Form from 'components/shared/Form';
import { InputField, SelectField } from 'components/features/MonitorView/FormFields';
import { useSyncFormStep } from '../../hooks';
import { acqModeValues, acqModeOptions, polarizationOptions, scsOptions, validatePCI, validateSsbArfcn } from './utils';
import styles from './AcquisitionInfoForm.module.css';

const defaultValues = {
  polarization: 'horizontal',
  acqMode: 'no-kpi',
  pci: undefined,
  ssbArfcn: undefined,
  scs: undefined
};

const defaultErrors = {
  pci: null,
  ssbArfcn: null
};

function AcquisitionInfoForm({ value }) {
  const dispatch = useDispatch();
  const deviceData = useSelector((state) => state.data.commissioningGA2.deviceData);
  const deviceProperties = deviceData?.properties?.reported;
  const [errors, setErrors] = useState(defaultErrors);
  const [isSaved, setIsSaved] = useState(false);
  const [key, setKey] = useState(Math.random());
  const methods = useForm({
    defaultValues
  });

  const { formState, reset, watch, setValue } = methods;

  const polarization = watch('polarization');
  const acqMode = watch('acqMode');
  const pci = watch('pci');
  const ssbArfcn = watch('ssbArfcn');
  const scs = watch('scs');

  const { isSubmitting, isValid, isDirty, isSubmitSuccessful } = formState;

  const handleSubmit = useCallback(
    (values) => {
      const { polarization, acqMode, pci, ssbArfcn, scs } = values;
      const enableKpis = acqMode === acqModeValues.WITH_KPI;
      const payload = {
        polarization,
        enableKpis,
        ...(enableKpis && {
          pci: parseInt(pci),
          'ssb-arfcn': parseInt(ssbArfcn),
          scs: parseInt(scs.match(/\((\d+)\s*kHz\)/)[1])
        })
      };
      dispatch(setCommissionDeviceAcquisition(payload));
    },
    [dispatch]
  );
  const handleReset = useCallback(() => {
    reset(defaultValues);
    setErrors(defaultErrors);
    dispatch(resetAcqInfo());
    setIsSaved(false);
    setKey(Math.random());
  }, [reset, dispatch]);

  const onKeyDown = (keyEvent) => {
    if ((keyEvent.charCode || keyEvent.keyCode) === 13) {
      keyEvent.preventDefault();
    }
  };

  const areKpiFieldsDisabled = useMemo(() => {
    return !acqMode || acqMode === acqModeValues.NO_KPI;
  }, [acqMode]);

  useSyncFormStep(value, formState);
  useEffect(() => {
    if (isSubmitSuccessful) {
      setIsSaved(true);
    }
  }, [isSubmitSuccessful, handleReset]);

  useEffect(() => {
    const productType = deviceProperties?.productType;
    if (!productType) return;

    if (pci === undefined && ssbArfcn === undefined) return;

    const isPciValid = validatePCI(pci);
    const isSsbArfcnValid = validateSsbArfcn(ssbArfcn, productType);

    if (!isBoolean(isPciValid)) {
      setErrors((state) => ({ ...state, pci: isPciValid }));
    }

    if (!isBoolean(isSsbArfcnValid)) {
      setErrors((state) => ({ ...state, ssbArfcn: isSsbArfcnValid }));
    }
  }, [pci, ssbArfcn, setErrors, deviceProperties]);

  const resetKpiFields = useCallback(() => {
    if (acqMode === acqModeValues.NO_KPI && (pci || ssbArfcn || scs)) {
      setValue('pci', undefined);
      setValue('ssbArfcn', undefined);
      setValue('scs', undefined);
      setErrors(defaultErrors);
      setKey(Math.random());
    }
  }, [acqMode, pci, ssbArfcn, scs, setValue]);

  useEffect(() => {
    resetKpiFields();
  }, [resetKpiFields]);

  const isSaveBtnDisabled = useMemo(() => {
    if (isSubmitting || !isValid || !isDirty || Object.values(errors).some(isString)) {
      return true;
    }

    if (!polarization || !acqMode) {
      return true;
    }

    if (acqMode === acqModeValues.NO_KPI && !polarization) {
      return true;
    }
    if (acqMode === acqModeValues.WITH_KPI && (!polarization || !pci || !ssbArfcn || !scs)) {
      return true;
    }

    return false;
  }, [isSubmitting, isValid, isDirty, errors, polarization, pci, ssbArfcn, scs, acqMode]);

  return (
    <Form methods={methods} onSubmit={handleSubmit} className={styles.root} onKeyDown={onKeyDown}>
      <div className={styles.inputFieldWrapper} data-testid="inputFieldWrapper">
        <SelectField
          key={`${key}-polarization`}
          name="polarization"
          valueKey="polarization"
          label="Polarization"
          placeholder="Select"
          disabled={isSaved}
          options={polarizationOptions}
          menuPlacement="bottom"
        />

        <SelectField
          key={`${key}-acqMode`}
          name="acqMode"
          valueKey="acqMode"
          label="Acquisition Mode"
          placeholder="Select"
          disabled={isSaved}
          options={acqModeOptions}
          menuPlacement="bottom"
        />

        <InputField
          key={`${key}-pci`}
          name="pci"
          label="PCI (Physical Cell ID)"
          placeholder=""
          errors={errors}
          setErrors={setErrors}
          disabled={areKpiFieldsDisabled || isSaved}
        />
        <InputField
          key={`${key}-ssbArfcn`}
          name="ssbArfcn"
          label="SSB-ARFCN"
          placeholder=""
          errors={errors}
          setErrors={setErrors}
          disabled={areKpiFieldsDisabled || isSaved}
        />
        <SelectField
          key={`${key}-scs`}
          name="scs"
          valueKey="scs"
          label="SCS (Sub-Carrier Spacing)"
          placeholder="Select"
          options={scsOptions}
          menuPlacement="bottom"
          disabled={areKpiFieldsDisabled || isSaved}
        />
      </div>
      <div className={styles.actions}>
        <Button type="reset" variant="link" size="medium" color="primary" disabled={!isDirty} onClick={handleReset}>
          Reset
        </Button>
        <Button type="submit" variant="contained" size="medium" color="primary" disabled={isSaveBtnDisabled || isSaved}>
          {isSaved ? 'Saved' : 'Save'}
        </Button>
      </div>
    </Form>
  );
}

export default AcquisitionInfoForm;
